import { useState, type FormEvent } from 'react';
import { useStore } from '@nanostores/react';
import { Banner, Button, Card, Heading, Link, Text, TextInput, VStack } from '@astryxdesign/core';
import { useNavigate } from 'react-router';

import { $auth, $authPending, register } from '../stores/auth';
import { usePageEvent } from '../analytics/usePageEvent';

const MIN_PASSWORD = 10;

export function RegisterPage() {
  usePageEvent('signup_started');
  const auth = useStore($auth);
  const pending = useStore($authPending);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Mirror the server rule client-side so the round trip is not wasted.
    if (password.length < MIN_PASSWORD) {
      setLocalError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setLocalError(null);
    try {
      await register({ email, password, name, company });
      await navigate('/app/keys', { replace: true });
    } catch {
      /* surfaced via the store */
    }
  }

  const error = localError ?? auth.error;

  return (
    <Card padding={4} variant="default" className="auth-card">
      <form onSubmit={onSubmit}>
        <VStack gap={3}>
          <VStack gap={0.5}>
            <Heading level={1}>Create your account</Heading>
            <Text size="sm" color="secondary">
              Starts on the Free plan: 300 forecast calls a month, no card required.
            </Text>
          </VStack>

          {error && <Banner status="error" title="Could not create account" description={error} />}

          <TextInput
            label="Work email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@company.com"
            isRequired
            hasAutoFocus
            width="100%"
          />
          <TextInput
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            description={`At least ${MIN_PASSWORD} characters.`}
            placeholder="••••••••••"
            isRequired
            width="100%"
          />
          <TextInput
            label="Your name"
            value={name}
            onChange={setName}
            placeholder="Alex Tran"
            isOptional
            width="100%"
          />
          <TextInput
            label="Company"
            value={company}
            onChange={setCompany}
            placeholder="Acme Logistics"
            isOptional
            width="100%"
          />

          <Button
            label="Create account"
            variant="primary"
            type="submit"
            width="100%"
            isLoading={pending}
          />

          <Text size="sm" color="secondary">
            Already have an account? <Link href="/login">Sign in</Link>.
          </Text>
        </VStack>
      </form>
    </Card>
  );
}
